import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounce as rxjsDebounce,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
  Subscription,
  timer,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { debounce } from 'ts-debounce';
import {
  EventCategory,
  SessionEvent,
  SessionEventSubscriber,
} from '../sessions/session/SessionEvent';

import { SpaceRepository } from '../state/space.repository';
import { PanelView } from '../sessions/panel/panel.component';
import { IconBackground } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { INFOS } from '../common/utils/notification-constants';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { proFeatureSubscriptionTypes, SUBSCRIPTION_TYPES } from '../models/payment';
import { User } from '../models/user';
import { HighlighterPipe } from '../pipes/highlighter.pipe';
import { ParticipantEventAction } from '../common/interfaces/rtc-interface';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { IconTypes } from '../standalones/components/pencil-icon/pencil-icon.component';
import { ForegroundActivityService } from './foreground-activity.service';
import { RealtimeSpaceService } from './realtime-space.service';
import { SessionSharedDataService } from './session-shared-data.service';
import {
  NotificationToasterService,
  NotificationType,
  NotificationActionButton,
  V2NotificationData,
  V2NotificationDataBuilder,
  NotificationIconTypes,
  NotificationIconConfig,
} from './notification-toaster.service';
import { ProviderStateService } from './provider-state.service';
import { FLAGS, FlagsService } from './flags.service';
import { PresenceProvider } from './presence-provider';
import { DevicesManagerService } from './devices-manager.service';
import { ScreenSizeService, ScreenSize } from './screen-size.service';

enum ForegroundActivityState {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}
interface InactivityRecord {
  inactive: boolean;
  name: string;
}
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ForegroundActivityNotificationsService implements SessionEventSubscriber {
  private WAIT_TIME_BEFORE_SENDING_INACTIVITY_STATUS = 15 * 1000; // 15 seconds
  private WAIT_TIME_BEFORE_SENDING_ACTIVITY_STATUS = 2 * 1000; // 2 seconds

  private localForegroundActivitySubscription?: Subscription;
  private currentSpaceSubscription?: Subscription;
  private participantsEventsSubscriptions?: Subscription;

  private usersForegroundInactivityState: Record<string, InactivityRecord> = {};

  private _userInactivityStatusChanged$ = new BehaviorSubject<string>('');
  public userInactivityStatusChanged$ = this._userInactivityStatusChanged$.asObservable();
  public readonly requiredSubscriptionTypes = proFeatureSubscriptionTypes;

  private readonly inactivityNotificationUIService: InactivityNotificationUIService;
  private debouncedSendInactivityEvent = debounce(this.sendInactivityEvent.bind(this), 2 * 1000);

  constructor(
    private foregroundInactivityService: ForegroundActivityService,
    private realtimeSpaceService: RealtimeSpaceService,
    private spaceRepository: SpaceRepository,
    private translateService: TranslateService,
    private sharedDataService: SessionSharedDataService,
    private notificationToasterService: NotificationToasterService,
    private providerStateService: ProviderStateService,
    private flagsService: FlagsService,
    private presenceProvider: PresenceProvider,
    private devicesManagerService: DevicesManagerService,
    private screenSizeService: ScreenSizeService,
  ) {
    this.inactivityNotificationUIService = new InactivityNotificationUIService(
      this.notificationToasterService,
      this.translateService,
      this.sharedDataService,
      this.spaceRepository,
      this.presenceProvider,
      this.screenSizeService.screenSize$.pipe(
        map((size) => size === ScreenSize.XSMALL),
        distinctUntilChanged(),
      ),
    );
  }

  public setUpInactivityNotifications(): void {
    combineLatest([
      this.flagsService.featureFlagChanged(FLAGS.INACTIVITY_NOTIFICATIONS),
      this.spaceRepository.isCurrentUserSpaceHost$,
      this.providerStateService.callConnected$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([inactivityNotificationsEnabled, isCurrentUserSpaceHost, callConnected]) => {
        if (!inactivityNotificationsEnabled) {
          return;
        }
        if (!callConnected) {
          this.stopInactivityNotifications();
        } else if (isCurrentUserSpaceHost) {
          this.startReceivingInactivityStatusAndStopSendingIt();
        } else {
          this.startSendingInactivityStatusAndStopReceivingIt();
        }
      });
  }

  public stopInactivityNotifications(): void {
    this.realtimeSpaceService.service.unSubscribeFromEvent(
      this,
      EventCategory.UsersForegroundActivity,
    );
    this.localForegroundActivitySubscription?.unsubscribe();
    this.currentSpaceSubscription?.unsubscribe();
    this.participantsEventsSubscriptions?.unsubscribe();

    this.inactivityNotificationUIService.dismissNotification();

    this.resetInactivityStatuses();

    this.usersForegroundInactivityState = {};
    this.inactivityNotificationUIService.clear();
  }

  private startReceivingInactivityStatusAndStopSendingIt(): void {
    // if user was a participant and promoted to host,
    // we should remove the inactivity indicator at the other hosts side
    this.sendInactivityEvent();

    const user = this.sharedDataService.user;

    const notificationsEnabledInUsersSettings =
      user?.institution?.settings?.inactivityNotifications ||
      user?.settings?.inactivityNotifications;

    if (!notificationsEnabledInUsersSettings || !this.userPaidForFeature(user)) {
      return;
    }

    this.realtimeSpaceService.service.subscribeToEvent(this, EventCategory.UsersForegroundActivity);

    this.participantsEventsSubscriptions = this.providerStateService.participantEvents$
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (event?.action === ParticipantEventAction.LEFT && event.participant.userId) {
          delete this.usersForegroundInactivityState[event.participant.userId];
          this.inactivityNotificationUIService.checkForNotificationTextChanges(
            this.usersForegroundInactivityState,
          );
        }
      });

    this.localForegroundActivitySubscription?.unsubscribe();
    this.currentSpaceSubscription?.unsubscribe();
  }

  private startSendingInactivityStatusAndStopReceivingIt(): void {
    this.realtimeSpaceService.service.unSubscribeFromEvent(
      this,
      EventCategory.UsersForegroundActivity,
    );
    this.participantsEventsSubscriptions?.unsubscribe();

    this.localForegroundActivitySubscription = combineLatest([
      this.foregroundInactivityService.isForegroundInactive$,
      this.devicesManagerService.isSharingScreen,
    ])
      .pipe(
        untilDestroyed(this),
        // A screensharing user is presenting, not distracted, so they are never inactive.
        map(([isForegroundInactive, isSharingScreen]) => isForegroundInactive && !isSharingScreen),
        distinctUntilChanged(),
        // Use shorter delay when user becomes active to quickly suppress the distraction indicator
        rxjsDebounce((isInactive) =>
          timer(
            isInactive
              ? this.WAIT_TIME_BEFORE_SENDING_INACTIVITY_STATUS
              : this.WAIT_TIME_BEFORE_SENDING_ACTIVITY_STATUS,
          ),
        ),
        distinctUntilChanged(),
      )
      .subscribe(() => this.debouncedSendInactivityEvent());

    // to send the inactivity event whenever a new user becomes a host
    this.currentSpaceSubscription = this.spaceRepository.activeSpace$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.debouncedSendInactivityEvent());

    // we need the call presence inorder to send our current status whenever a user joins the call
    this.participantsEventsSubscriptions = this.providerStateService.participantEvents$
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (event?.action === ParticipantEventAction.JOINED) {
          this.debouncedSendInactivityEvent();
        }
      });

    // removing the status and notification for the case of hosts that are demoted for participants
    this.resetInactivityStatuses();
    this.inactivityNotificationUIService.dismissNotification();
  }

  private sendInactivityEvent(): void {
    // Host can't be considered inactive because this feature is only to show inactive participants.
    // A screensharing user is actively presenting, so we never report them as inactive; instead we
    // send ACTIVE, clearing any previous distracted state.
    const foregroundInactive =
      !this.spaceRepository.isCurrentUserSpaceHost &&
      this.foregroundInactivityService.isForegroundInactive &&
      !this.devicesManagerService.isSharingScreen.value;

    this.realtimeSpaceService.service.sendEvent(
      this,
      new SessionEvent(
        EventCategory.UsersForegroundActivity,
        foregroundInactive ? ForegroundActivityState.INACTIVE : ForegroundActivityState.ACTIVE,
        {
          userId: this.sharedDataService.user?._id,
          userName: this.sharedDataService.user?.name,
          roomId: this.spaceRepository.activeSpaceCurrentRoom?.uid,
        },
      ),
    );
  }

  sessionEventReceived(event: SessionEvent, userId: string): void {
    if (
      event.category !== EventCategory.UsersForegroundActivity ||
      !this.spaceRepository.isCurrentUserSpaceHost ||
      !event.data.userId ||
      !event.data.userName ||
      event.data.roomId !== this.spaceRepository.activeSpaceCurrentRoom?.uid
    ) {
      return;
    }

    const inactive = event.type === ForegroundActivityState.INACTIVE;

    if (this.usersForegroundInactivityState[event.data.userId]?.inactive === inactive) {
      // ignore unnecessary updates
      // because they might have been sent from a participant to initialize the state of a newly joined host
      return;
    }

    this.usersForegroundInactivityState[event.data.userId] = {
      inactive: inactive,
      name: event.data.userName,
    };

    this._userInactivityStatusChanged$.next(event.data.userId);
    this.inactivityNotificationUIService.checkForNotificationTextChanges(
      this.usersForegroundInactivityState,
    );
  }

  private resetInactivityStatuses(): void {
    // resetting the status of all users to be active and publishing that change to front-end components
    for (const userId of Object.keys(this.usersForegroundInactivityState)) {
      delete this.usersForegroundInactivityState[userId];
      this._userInactivityStatusChanged$.next(userId);
    }
    this.usersForegroundInactivityState = {};
  }

  public getUserInactivityObservable(userId: string): Observable<boolean> {
    return this.userInactivityStatusChanged$.pipe(
      untilDestroyed(this),
      filter((currentUserId) => currentUserId === userId),
      map(() => this.getInactivityState()[userId]?.inactive ?? false),
      startWith(this.getInactivityState()[userId]?.inactive ?? false),
    );
  }

  public getInactivityState() {
    return this.usersForegroundInactivityState;
  }

  public userPaidForFeature(user: User | undefined): boolean {
    return this.requiredSubscriptionTypes.includes(user?.subscriptionType as SUBSCRIPTION_TYPES);
  }
}

class InactivityNotificationUIService {
  private TEMPORARY_MUTE_DURATION = 5 * 60 * 1000; // 5 minutes
  private MIN_USERS_TO_HIDE_NOTIFICATIONS = 5;
  private lastInactiveParticipantsIds: Set<string> = new Set([]);

  private notificationsMuted = false;

  private lastNotificationParticipantsManagerAlreadyOpened = false;
  private lastInactiveParticipantsNotificationData: V2NotificationData | undefined;

  private isMobile = false;
  private isMobileSubscription: Subscription | undefined;

  constructor(
    private notificationToasterService: NotificationToasterService,
    private translateService: TranslateService,
    private sharedDataService: SessionSharedDataService,
    private spaceRepository: SpaceRepository,
    private presenceProvider: PresenceProvider,
    isMobile$: Observable<boolean>,
  ) {
    // Subscribe to isMobile$ to track mobile state changes
    this.isMobileSubscription = isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
      // If transitioning to mobile while notification is active, dismiss it
      if (isMobile && this.isNotificationActive()) {
        this.dismissNotification();
      }
    });
  }

  public checkForNotificationTextChanges = debounce(
    this._checkForNotificationTextChanges.bind(this),
    2 * 1000,
  );

  public dismissNotification(): void {
    this.notificationToasterService.dismissNotificationsByCode([INFOS.PARTICIPANTS_INACTIVE]);
  }

  public clear() {
    this.lastInactiveParticipantsIds.clear();
    this.isMobileSubscription?.unsubscribe();
  }

  private _checkForNotificationTextChanges(
    usersForegroundInactivityState: Record<string, InactivityRecord>,
  ): void {
    const inactiveParticipants = Object.values(usersForegroundInactivityState).filter(
      (inactivityRecord) => inactivityRecord.inactive,
    );

    if (inactiveParticipants.length === 0) {
      this.dismissNotification();
      this.lastInactiveParticipantsIds.clear();
    } else {
      const inactiveParticipantsIds = Object.entries(usersForegroundInactivityState)
        .filter(([_, inactivityRecord]) => inactivityRecord.inactive)
        .map(([participantId]) => participantId);

      const newInactiveParticipantsIds = inactiveParticipantsIds.filter(
        (inactiveParticipantId) => !this.lastInactiveParticipantsIds.has(inactiveParticipantId),
      );

      // we show the notification only if:
      //  - a participant became inactive or when it is already showing
      //  - a participant is no longer inactive only if the notification is already showing (to update its content)
      if (newInactiveParticipantsIds.length > 0) {
        this.showInactivityNotification(inactiveParticipants);
      } else if (
        this.isNotificationActive() &&
        this.lastInactiveParticipantsIds.size !== inactiveParticipantsIds.length
      ) {
        this.showInactivityNotification(inactiveParticipants);
      }
      this.lastInactiveParticipantsIds = new Set(inactiveParticipantsIds);
    }
  }

  private showInactivityNotification(inactiveParticipants: InactivityRecord[]) {
    // Do not show distracted participants notification on mobile - it takes too much screen space
    if (this.isMobile) {
      return;
    }

    const activeSpaceId = this.spaceRepository.activeSpaceId;
    const activeSpaceCurrentRoomId = this.spaceRepository.activeSpaceCurrentRoom?.uid;

    if (this.notificationsMuted || !activeSpaceId || !activeSpaceCurrentRoomId) {
      return;
    }

    // Do not show notification if there are more or equal to MIN_USERS_TO_HIDE_NOTIFICATIONS users in the room
    const usersInRoom = this.presenceProvider.getRoomPresenceData(
      activeSpaceId,
      activeSpaceCurrentRoomId,
    );
    if (usersInRoom.size >= this.MIN_USERS_TO_HIDE_NOTIFICATIONS) {
      return;
    }

    const notificationMessage = this.getNotificationMessage(inactiveParticipants);
    const participantsManagerAlreadyOpened =
      this.sharedDataService.rightPanelView.getValue()?.panelView === PanelView.participantsManager;

    // if notification already show, and it is only a message change
    if (
      this.isNotificationActive() &&
      this.lastInactiveParticipantsNotificationData &&
      participantsManagerAlreadyOpened === this.lastNotificationParticipantsManagerAlreadyOpened
    ) {
      this.lastInactiveParticipantsNotificationData.description = notificationMessage;
    } else {
      this.lastInactiveParticipantsNotificationData = this.getNotificationData(
        notificationMessage,
        participantsManagerAlreadyOpened,
      );
      this.dismissNotification();
      this.notificationToasterService.showNotification(
        this.lastInactiveParticipantsNotificationData,
      );
    }
    this.lastNotificationParticipantsManagerAlreadyOpened = participantsManagerAlreadyOpened;
  }

  private getNotificationData(message: string, participantsManagerAlreadyOpened: boolean) {
    const notificationTitle = this.translateService.instant('Distracted participants');

    const notificationIcon: NotificationIconConfig = {
      type: NotificationIconTypes.SVG_ICON,
      background: IconBackground.INFO,
      assetName: 'bed-icon',
      height: 16,
      width: 16,
    };

    const primaryNotificationAction: NotificationActionButton = {
      text: this.translateService.instant('View'),
      onClick: () => {
        this.sharedDataService.changeRightPanelView.next(PanelView.participantsManager);
      },
    };

    const secondaryNotificationAction: NotificationActionButton = {
      text: this.translateService.instant('Mute for 5 min'),
      icon: 'bell',
      iconConfig: {
        type: IconTypes.svgIcon,
      },
      onClick: () => this.muteNotifications(),
      options: {
        items: [
          {
            text: this.translateService.instant('Mute for session'),
            onClick: () => this.muteNotifications(true),
          },
        ],
      },
    };

    return new V2NotificationDataBuilder(INFOS.PARTICIPANTS_INACTIVE)
      .type(NotificationType.INFO)
      .style(ToasterPopupStyle.INFO)
      .dismissable(true)
      .title(notificationTitle)
      .icon(notificationIcon)
      .description(message)
      .actionButtonsConfig({
        primary: !participantsManagerAlreadyOpened ? primaryNotificationAction : undefined,
        secondary: secondaryNotificationAction,
      })
      .priority(590)
      .build();
  }

  private getNotificationMessage(inactiveParticipants: InactivityRecord[]): string {
    const numberOfInactiveParticipants = inactiveParticipants.length;
    let otherInactiveUsersString = '';
    if (numberOfInactiveParticipants > 1) {
      otherInactiveUsersString = `${
        numberOfInactiveParticipants - 1
      } ${this.translateService.instant('others')}`;

      if (numberOfInactiveParticipants == 2) {
        otherInactiveUsersString = inactiveParticipants[1].name;
      }
      otherInactiveUsersString = `${this.translateService.instant(
        'and',
      )} ${otherInactiveUsersString}`;
    }
    const notificationSuffix = ` ${this.translateService.instant(
      numberOfInactiveParticipants > 1
        ? 'do not have Pencil Spaces active and may be distracted'
        : 'does not have Pencil Spaces active and may be distracted',
    )}.`;
    const notificationText = `${inactiveParticipants[0].name} ${otherInactiveUsersString} ${notificationSuffix}`;

    return new HighlighterPipe().transform(
      notificationText,
      inactiveParticipants
        .splice(0, Math.min(inactiveParticipants.length, 2))
        .map((record) => record.name),
      'array',
    ) as string;
  }

  private muteNotifications(entireSession = false) {
    this.notificationsMuted = true;
    this.dismissNotification();
    if (!entireSession) {
      modifiedSetTimeout(() => (this.notificationsMuted = false), this.TEMPORARY_MUTE_DURATION);
    }
  }

  private isNotificationActive() {
    return this.notificationToasterService.checkIfNotificationActiveByCode(
      INFOS.PARTICIPANTS_INACTIVE,
    );
  }
}
